import React, { useEffect, useState } from 'react';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, badgeAPI } from '../utils/api';
import { toast } from 'sonner';
import * as LucideIcons from 'lucide-react';

const CATEGORY_RING = {
  bronze: 'from-orange-200 to-orange-100 border-orange-300 text-orange-800',
  silver: 'from-slate-200 to-slate-100 border-slate-300 text-slate-800',
  gold: 'from-amber-200 to-amber-100 border-amber-400 text-amber-800',
  platinum: 'from-violet-200 to-violet-100 border-violet-400 text-violet-800',
};

const MODULE_LABEL_TR = {
  elifba: 'Elif-Ba',
  hifz: 'Hafızlık',
  hatim: 'Hatim',
  streak: 'Süreklilik',
  reading: 'Okuma',
};

const Badges = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, all, mine, _check] = await Promise.all([
          authAPI.getMe(),
          badgeAPI.getAllBadges(),
          badgeAPI.getUserBadges(),
          badgeAPI.checkBadges().catch(() => null),
        ]);
        if (cancelled) return;
        setUser(u.data);
        setAllBadges(all.data || []);
        // Re-fetch user badges after check (might have new ones)
        try {
          const refreshed = await badgeAPI.getUserBadges();
          if (!cancelled) setMyBadges(refreshed.data || []);
        } catch {
          if (!cancelled) setMyBadges(mine.data || []);
        }
      } catch {
        if (!cancelled) toast.error(lang === 'tr' ? 'Rozetler yüklenemedi' : 'Failed to load badges');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  const earnedIds = new Set(myBadges.map((b) => b.badge_id));
  const grouped = allBadges.reduce((acc, b) => {
    const k = b.module || 'misc';
    acc[k] = acc[k] || [];
    acc[k].push(b);
    return acc;
  }, {});

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Rozetlerim' : 'My Badges'}>
      <div className="max-w-5xl mx-auto p-4 sm:p-8 md:p-12 space-y-8">
        <header className="mb-2">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{lang === 'tr' ? 'Başarılar' : 'Achievements'}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Rozetlerim' : 'My Badges'}
          </h1>
          <p className="text-stone-600 mt-2">
            {lang === 'tr'
              ? `${myBadges.length} / ${allBadges.length} rozet kazanıldı. Çalıştıkça açıyorsun!`
              : `${myBadges.length} / ${allBadges.length} earned. Keep practicing to unlock more!`}
          </p>
        </header>

        {loading ? (
          <div className="text-center py-12 text-stone-500">{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
        ) : (
          Object.keys(grouped).map((moduleKey) => (
            <section key={moduleKey} data-testid={`badge-section-${moduleKey}`}>
              <h2 className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-3 font-semibold">
                {MODULE_LABEL_TR[moduleKey] || moduleKey}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {grouped[moduleKey].map((b) => {
                  const earned = earnedIds.has(b.id);
                  const Icon = LucideIcons[toComponentName(b.icon)] || LucideIcons.Award;
                  return (
                    <div
                      key={b.id}
                      data-testid={`badge-${b.id}`}
                      className={`relative p-5 rounded-2xl border-2 bg-gradient-to-b transition-all ${earned ? CATEGORY_RING[b.category] || CATEGORY_RING.silver : 'from-stone-50 to-white border-stone-200 text-stone-400'}`}
                    >
                      <div className={`w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3 ${earned ? 'bg-white shadow-md' : 'bg-stone-100 opacity-50'}`}>
                        <Icon className="w-7 h-7" strokeWidth={earned ? 2.2 : 1.6} />
                      </div>
                      <h3 className="text-center text-sm font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {lang === 'en' ? (b.name_en || b.name) : b.name}
                      </h3>
                      <p className={`text-center text-xs leading-snug ${earned ? '' : 'text-stone-400'}`}>
                        {lang === 'en' ? (b.description_en || b.description) : b.description}
                      </p>
                      {earned && (
                        <span className="absolute top-2 right-2 text-xs font-bold px-1.5 py-0.5 rounded-full bg-white/90 text-emerald-700 shadow">
                          ✓
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </AppShell>
  );
};

function toComponentName(kebab) {
  // 'book-open' → 'BookOpen'
  return (kebab || '').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

export default Badges;
